import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Toast — Radix's, wrapped, with a queue.
 *
 * A toast confirms what just happened ("Room locked. 14 stations.") and must be announced without
 * stealing focus, which is what Radix's live region does. Two details this wrapper insists on:
 *
 *   - An error toast does not time out. Something that failed cannot vanish after four seconds.
 *   - Every toast can be dismissed from the keyboard; F6 moves focus to the region, and the close
 *     control has a real name.
 */
import * as RadixToast from '@radix-ui/react-toast';
import { createContext, useCallback, useContext, useMemo, useRef, useState, } from 'react';
import { useUiText } from '@clavian/i18n/react';
import { cx } from '../lib/cx.js';
import { Button } from './button.js';
import { CheckIcon, CloseIcon } from './icons.js';
import { Impact } from './motion.js';
const ToastContext = createContext(null);
/** A day, which is "until someone deals with it" for a screen that is watched by a shift. */
const PERSISTENT_MS = 24 * 60 * 60 * 1000;
export function ToastProvider({ children, durationMs = 5000 }) {
    const t = useUiText();
    const [toasts, setToasts] = useState([]);
    const nextId = useRef(1);
    const dismiss = useCallback((id) => {
        setToasts((current) => current.filter((toast) => toast.id !== id));
    }, []);
    const show = useCallback((toast) => {
        const id = nextId.current;
        nextId.current += 1;
        setToasts((current) => [...current, { ...toast, id }]);
    }, []);
    const api = useMemo(() => ({ show, dismiss }), [show, dismiss]);
    return (_jsx(ToastContext.Provider, { value: api, children: _jsxs(RadixToast.Provider, { duration: durationMs, label: t('toast.region'), children: [children, toasts.map((toast) => (_jsxs(RadixToast.Root, { className: cx('clav-toast', toast.tone && toast.tone !== 'neutral' && `clav-toast--${toast.tone}`), duration: toast.tone === 'danger' ? PERSISTENT_MS : toast.durationMs, onOpenChange: (open) => {
                        if (!open)
                            dismiss(toast.id);
                    }, children: [toast.tone === 'ok' ? (_jsx("span", { className: "clav-toast__impact", "aria-hidden": "true", children: _jsx(Impact, { size: 28, burst: toast.id, children: _jsx(CheckIcon, {}) }) })) : null, _jsx(RadixToast.Title, { className: "clav-toast__title", children: toast.title }), toast.description ? (_jsx(RadixToast.Description, { className: "clav-toast__description", children: toast.description })) : null, _jsx("div", { className: "clav-toast__action", children: toast.action ? (_jsx(RadixToast.Action, { asChild: true, altText: toast.action.label, children: _jsx(Button, { variant: "ghost", size: "sm", onClick: toast.action.onAction, children: toast.action.label }) })) : (_jsx(RadixToast.Close, { asChild: true, children: _jsx(Button, { variant: "ghost", size: "sm", label: t('dismiss'), icon: _jsx(CloseIcon, {}) }) })) })] }, toast.id))), _jsx(RadixToast.Viewport, { className: "clav-toast__viewport" })] }) }));
}
/** Raise a toast. Throws outside the provider rather than dropping the message silently. */
export function useToast() {
    const api = useContext(ToastContext);
    if (!api)
        throw new Error('useToast() needs a <ToastProvider> above it.');
    return api;
}
//# sourceMappingURL=toast.js.map